import React, { useEffect, useRef, useState, useCallback } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Clock, 
  Phone, 
  ArrowUp,
  CornerUpLeft,
  CornerUpRight,
  MapPin,
  CheckCircle,
  X,
  ChevronUp,
  ChevronDown,
  Navigation,
  Flag,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
  User
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Helper: parse duration from various formats the Routes API may return
const parseDurationSec = (d) => {
  if (typeof d === 'number') return d;
  if (typeof d === 'string') { const m = d.match(/(\d+)/); return m ? parseInt(m[1]) : 0; }
  if (d?.value != null) return d.value;
  return 0;
};

// Route display with turn-by-turn steps — tries new Route API, falls back to legacy
const RouteWithSteps = ({ pickup, dropoff, onRouteReady, driverMarkerRef }) => {
  const map = useMap();
  const routesLibrary = useMapsLibrary('routes');
  const polylinesRef = useRef([]);

  useEffect(() => {
    if (!map || !routesLibrary || !pickup?.lat || !pickup?.lng || !dropoff?.lat || !dropoff?.lng) return;

    let active = true;
    const origin = { lat: pickup.lat, lng: pickup.lng };
    const dest = { lat: dropoff.lat, lng: dropoff.lng };

    const cleanup = () => {
      polylinesRef.current.forEach(p => p.setMap(null));
      polylinesRef.current = [];
    };

    const applyResult = (path, info) => {
      if (!active) return;
      cleanup();
      const polyline = new google.maps.Polyline({
        path, strokeColor: '#3b82f6', strokeWeight: 8, strokeOpacity: 1, map
      });
      polylinesRef.current = [polyline];
      const bounds = new google.maps.LatLngBounds();
      path.forEach(p => bounds.extend(p));
      map.fitBounds(bounds, { padding: { top: 150, bottom: 250, left: 50, right: 50 } });
      if (onRouteReady) onRouteReady(info);
    };

    const buildStepLegacy = (step) => {
      const instr = (step.instructions || '').replace(/<[^>]*>/g, '');
      return {
        instruction: instr,
        distance: step.distance?.value || 0,
        distanceText: step.distance?.text || '',
        duration: step.duration?.value || 0,
        maneuver: { type: step.maneuver || 'straight', instruction: instr },
        name: ''
      };
    };

    const buildStepNew = (step) => {
      const instr = (step.instructions || '').replace(/<[^>]*>/g, '');
      const distM = step.distanceMeters || 0;
      const durS = parseDurationSec(step.staticDuration || step.duration);
      return {
        instruction: instr,
        distance: distM,
        distanceText: step.localizedValues?.distance || (distM < 1000 ? `${distM} m` : `${(distM / 1000).toFixed(1)} km`),
        duration: durS,
        maneuver: { type: step.maneuver || 'straight', instruction: instr },
        name: ''
      };
    };

    const computeRoute = async () => {
      // Try new Route API
      if (routesLibrary.Route) {
        try {
          const { routes } = await routesLibrary.Route.computeRoutes({
            origin, destination: dest, travelMode: 'DRIVING', fields: ['legs', 'path'],
          });
          if (routes?.length) {
            const leg = routes[0].legs?.[0];
            const totalDist = leg?.distanceMeters || 0;
            const totalDur = parseDurationSec(leg?.duration);
            applyResult(routes[0].path || [], {
              distance: totalDist,
              duration: totalDur,
              distanceText: leg?.localizedValues?.distance || `${(totalDist / 1000).toFixed(1)} km`,
              durationText: leg?.localizedValues?.staticDuration || `${Math.round(totalDur / 60)} min`,
              steps: leg?.steps?.map(buildStepNew) || []
            });
            return;
          }
        } catch (err) {
          console.warn('Routes API unavailable, using legacy Directions API:', err.message);
        }
      }

      // Fallback: legacy DirectionsService
      const service = new routesLibrary.DirectionsService();
      service.route({ origin, destination: dest, travelMode: google.maps.TravelMode.DRIVING }, (result, status) => {
        if (!active || status !== google.maps.DirectionsStatus.OK || !result) return;
        const route = result.routes[0];
        const leg = route.legs[0];
        applyResult(route.overview_path, {
          distance: leg.distance?.value || 0,
          duration: leg.duration?.value || 0,
          distanceText: leg.distance?.text || '',
          durationText: leg.duration?.text || '',
          steps: leg.steps?.map(buildStepLegacy) || []
        });
      });
    };

    computeRoute();
    return () => { active = false; cleanup(); };
  }, [map, routesLibrary, pickup, dropoff, onRouteReady]);

  return null;
};

/**
 * Full In-App Navigation Component with Lyft-style turn-by-turn directions
 * and Voice Navigation using Web Speech API
 */
const DriverNavigationView = ({ 
  ride, 
  driverLocation,
  onStatusUpdate, 
  onCall,
  onClose,
  isUpdating 
}) => {
  const driverMarker = useRef(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [allSteps, setAllSteps] = useState([]);
  const [navigationPhase, setNavigationPhase] = useState('to_pickup');
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [mapError, setMapError] = useState(null);
  const [showRiderInfo, setShowRiderInfo] = useState(true);
  const [showInstruction, setShowInstruction] = useState(true);
  const instructionTimerRef = useRef(null);
  
  // Voice Navigation State
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const lastAnnouncedStep = useRef(-1);
  const speechSynthesis = useRef(null);
  const hasAnnouncedStart = useRef(false);

  // Initialize speech synthesis
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      speechSynthesis.current = window.speechSynthesis;
      setVoiceSupported(true);
      
      // Load voices
      const loadVoices = () => {
        speechSynthesis.current.getVoices();
      };
      loadVoices();
      speechSynthesis.current.addEventListener('voiceschanged', loadVoices);
      
      return () => {
        if (speechSynthesis.current) {
          speechSynthesis.current.cancel();
          speechSynthesis.current.removeEventListener('voiceschanged', loadVoices);
        }
      };
    }
  }, []);

  // Speak function for voice navigation
  const speak = useCallback((text, priority = 'normal') => {
    if (!voiceEnabled || !speechSynthesis.current || !text) return;
    
    if (priority === 'high') {
      speechSynthesis.current.cancel();
    }
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    utterance.lang = 'en-US';
    
    const voices = speechSynthesis.current.getVoices();
    const preferredVoice = voices.find(v => 
      v.lang.startsWith('en') && (v.name.includes('Google') || v.name.includes('Natural') || v.name.includes('Samantha'))
    ) || voices.find(v => v.lang.startsWith('en'));
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    speechSynthesis.current.speak(utterance);
  }, [voiceEnabled]);

  // Announce navigation start
  useEffect(() => {
    if (!voiceEnabled || hasAnnouncedStart.current || !routeInfo || isLoading) return;
    
    const destination = navigationPhase === 'to_pickup' ? ride?.pickup : ride?.dropoff;
    const duration = routeInfo ? formatDuration(routeInfo.duration) : '';
    
    const announcement = navigationPhase === 'to_pickup' 
      ? `Starting navigation to pickup point. ${duration} away. ${allSteps[0]?.instruction || ''}`
      : `Starting navigation to drop-off. ${duration} away. ${allSteps[0]?.instruction || ''}`;
    
    speak(announcement, 'high');
    hasAnnouncedStart.current = true;
    lastAnnouncedStep.current = 0;
  }, [voiceEnabled, routeInfo, isLoading, navigationPhase, ride, allSteps, speak]);

  // Announce current step when it changes
  useEffect(() => {
    if (!voiceEnabled || !allSteps.length || currentStepIndex === lastAnnouncedStep.current || currentStepIndex === 0) return;
    
    const currentStep = allSteps[currentStepIndex];
    if (currentStep?.instruction) {
      speak(currentStep.instruction, 'high');
      lastAnnouncedStep.current = currentStepIndex;
    }
  }, [currentStepIndex, allSteps, voiceEnabled, speak]);

  // Toggle voice navigation
  const toggleVoice = useCallback(() => {
    if (voiceEnabled && speechSynthesis.current) {
      speechSynthesis.current.cancel();
    }
    setVoiceEnabled(!voiceEnabled);
    
    if (!voiceEnabled) {
      setTimeout(() => speak('Voice navigation enabled', 'high'), 100);
    }
  }, [voiceEnabled, speak]);

  // Auto-dismiss instruction banner after 5 seconds, re-show on step change
  const resetInstructionTimer = useCallback(() => {
    if (instructionTimerRef.current) clearTimeout(instructionTimerRef.current);
    setShowInstruction(true);
    instructionTimerRef.current = setTimeout(() => setShowInstruction(false), 5000);
  }, []);

  useEffect(() => {
    resetInstructionTimer();
    return () => { if (instructionTimerRef.current) clearTimeout(instructionTimerRef.current); };
  }, [currentStepIndex, resetInstructionTimer]);

  // Determine navigation phase based on ride status
  useEffect(() => {
    if (ride?.status === 'in_progress') {
      setNavigationPhase('to_dropoff');
      hasAnnouncedStart.current = false; // Reset for new phase
    } else {
      setNavigationPhase('to_pickup');
    }
  }, [ride?.status]);

  // Handle route ready callback
  const handleRouteReady = useCallback((info) => {
    setRouteInfo(info);
    setAllSteps(info.steps || []);
    setCurrentStepIndex(0);
    setIsLoading(false);
    setMapError(null);
  }, []);

  // Get maneuver icon based on maneuver string
  const getManeuverIcon = (maneuver, size = 'h-8 w-8') => {
    if (!maneuver) return <ArrowUp className={size} />;
    
    const type = maneuver.type?.toLowerCase() || '';
    
    if (type.includes('left')) return <CornerUpLeft className={size} />;
    if (type.includes('right')) return <CornerUpRight className={size} />;
    if (type.includes('uturn') || type.includes('u-turn')) return <RotateCcw className={size} />;
    if (type.includes('roundabout')) return <RotateCw className={size} />;
    if (type === 'arrive' || type.includes('destination')) return <Flag className={size} />;
    
    return <ArrowUp className={size} />;
  };

  // Format distance for display
  const formatDistance = (meters) => {
    if (!meters) return '';
    if (meters < 100) return `${Math.round(meters)} m`;
    if (meters < 1000) return `${Math.round(meters / 10) * 10} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  };

  // Format duration for display
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    if (seconds < 60) return `${Math.round(seconds)} sec`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    return `${hours} hr ${mins} min`;
  };

  // Get destination based on navigation phase
  const getDestination = () => {
    if (navigationPhase === 'to_pickup') {
      return {
        lat: ride?.pickupCoords?.lat || ride?.pickupCoords?.latitude || ride?.pickupLocation?.lat || ride?.pickupLocation?.latitude,
        lng: ride?.pickupCoords?.lng || ride?.pickupCoords?.longitude || ride?.pickupLocation?.lng || ride?.pickupLocation?.longitude
      };
    }
    return {
      lat: ride?.dropoffCoords?.lat || ride?.dropoffCoords?.latitude || ride?.dropoffLocation?.lat || ride?.dropoffLocation?.latitude,
      lng: ride?.dropoffCoords?.lng || ride?.dropoffCoords?.longitude || ride?.dropoffLocation?.lng || ride?.dropoffLocation?.longitude
    };
  };

  // Get current location (driver's position)
  const getCurrentLocation = () => {
    // If we have actual driver location, use it
    if (driverLocation) {
      return {
        lat: driverLocation.latitude || driverLocation.lat,
        lng: driverLocation.longitude || driverLocation.lng
      };
    }
    
    // For "to_pickup" phase, simulate driver being nearby (offset from pickup)
    // This ensures the route calculation works even without real GPS
    if (navigationPhase === 'to_pickup') {
      const pickupLat = ride?.pickupCoords?.lat || ride?.pickupCoords?.latitude || ride?.pickupLocation?.lat || ride?.pickupLocation?.latitude || -10.2129;
      const pickupLng = ride?.pickupCoords?.lng || ride?.pickupCoords?.longitude || ride?.pickupLocation?.lng || ride?.pickupLocation?.longitude || 31.1802;
      
      // Offset driver location by ~500m to simulate being nearby
      return {
        lat: pickupLat + 0.005,  // ~500m north
        lng: pickupLng - 0.003   // ~300m west
      };
    }
    
    // For "to_dropoff" phase, use pickup as starting point (driver picked up customer there)
    return {
      lat: ride?.pickupCoords?.lat || ride?.pickupCoords?.latitude || ride?.pickupLocation?.lat || ride?.pickupLocation?.latitude || -10.2129,
      lng: ride?.pickupCoords?.lng || ride?.pickupCoords?.longitude || ride?.pickupLocation?.lng || ride?.pickupLocation?.longitude || 31.1802
    };
  };

  // Handle call customer
  const handleCallCustomer = () => {
    const phone = ride?.userPhone;
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else if (onCall) {
      onCall(phone);
    }
  };

  const currentLocation = getCurrentLocation();
  const destination = getDestination();
  const currentStep = allSteps[currentStepIndex];

  // Check if we have valid coordinates
  if (!destination?.lat || !destination?.lng) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center z-50">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading navigation...</p>
        </div>
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center z-50">
        <div className="text-center text-red-400">
          <p>Google Maps API key not configured</p>
          <Button onClick={onClose} className="mt-4">Close</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col">
      {/* Top Navigation Bar */}
      <div className="bg-gray-800 text-white p-4 flex items-center justify-between z-20">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-gray-700">
            <X className="h-6 w-6" />
          </Button>
          <div>
            <p className="font-semibold">
              {navigationPhase === 'to_pickup' ? 'Navigating to Pickup' : 'Navigating to Drop-off'}
            </p>
            <p className="text-sm text-gray-400 truncate max-w-[200px]">
              {navigationPhase === 'to_pickup' ? ride?.pickup : ride?.dropoff}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Voice Toggle Button */}
          {voiceSupported && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleVoice}
              className={`text-white ${voiceEnabled ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-gray-700'}`}
              title={voiceEnabled ? 'Disable voice navigation' : 'Enable voice navigation'}
              data-testid="voice-toggle-btn"
            >
              {voiceEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </Button>
          )}
          {/* Call Customer Button */}
          {ride?.userPhone && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleCallCustomer} 
              className="text-white hover:bg-gray-700"
              data-testid="call-customer-btn"
            >
              <Phone className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Current Instruction - Compact banner with auto-dismiss */}
      {currentStep && !isLoading && showInstruction && (
        <div 
          className="absolute top-16 left-3 right-3 z-10 animate-in slide-in-from-top duration-300"
          onClick={() => setShowInstruction(false)}
        >
          <div className="bg-blue-600 text-white px-3 py-2 rounded-xl shadow-lg flex items-center gap-3">
            <div className="bg-white/20 p-1.5 rounded-lg shrink-0">
              {getManeuverIcon(currentStep.maneuver, 'h-5 w-5')}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight">{currentStep.distanceText || formatDistance(currentStep.distance)}</p>
              <p className="text-xs opacity-90 truncate">{currentStep.instruction || 'Follow the route'}</p>
            </div>
            <X className="h-4 w-4 opacity-60 shrink-0" />
          </div>
        </div>
      )}
      {currentStep && !isLoading && !showInstruction && (
        <button
          className="absolute top-16 left-3 z-10 bg-blue-600 text-white p-2 rounded-full shadow-lg"
          onClick={() => { setShowInstruction(true); resetInstructionTimer(); }}
          data-testid="show-instruction-btn"
        >
          {getManeuverIcon(currentStep.maneuver, 'h-5 w-5')}
        </button>
      )}

      {/* Map Container */}
      <div className="flex-1 relative">
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <Map
            defaultZoom={15}
            defaultCenter={currentLocation}
            mapId="tekstar_driver_navigation"
            gestureHandling="greedy"
            disableDefaultUI={true}
            zoomControl={false}
            style={{ width: '100%', height: '100%' }}
          >
            <RouteWithSteps 
              pickup={currentLocation}
              dropoff={destination}
              onRouteReady={handleRouteReady}
              driverMarkerRef={driverMarker}
            />
            
            {/* Driver Marker */}
            <AdvancedMarker position={currentLocation}>
              <div className="relative">
                <div className="w-12 h-12 bg-blue-600 rounded-full border-4 border-white shadow-xl flex items-center justify-center">
                  <Navigation className="h-6 w-6 text-white" />
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-blue-400 rounded-full opacity-30 animate-ping"></div>
              </div>
            </AdvancedMarker>
            
            {/* Destination Marker */}
            <AdvancedMarker position={destination}>
              <div className="w-10 h-10 bg-red-500 rounded-full border-3 border-white shadow-lg flex items-center justify-center">
                <MapPin className="h-6 w-6 text-white" />
              </div>
            </AdvancedMarker>
          </Map>
        </APIProvider>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-gray-900/80 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p>Loading route...</p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Panel - Added extra padding for "Made with Emergent" overlay */}
      <div className="bg-white rounded-t-3xl shadow-2xl z-20 pb-16">
        {/* Rider Info Section */}
        {showRiderInfo && (ride?.userName || ride?.userPhone) && (
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-pink-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{ride?.userName || 'Customer'}</p>
                  <p className="text-sm text-gray-600">{ride?.userPhone || 'No phone'}</p>
                </div>
              </div>
              {ride?.userPhone && (
                <Button
                  onClick={handleCallCustomer}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  size="sm"
                  data-testid="call-rider-btn"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ETA and Distance */}
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-gray-500" />
                <span className="text-xl font-bold">
                  {routeInfo ? formatDuration(routeInfo.duration) : '--'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Navigation className="h-5 w-5 text-gray-500" />
                <span className="text-lg text-gray-600">
                  {routeInfo ? formatDistance(routeInfo.distance) : '--'}
                </span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllSteps(!showAllSteps)}
              className="text-blue-600"
            >
              {showAllSteps ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
              <span className="ml-1">Steps</span>
            </Button>
          </div>
        </div>

        {/* All Steps (Expandable) */}
        {showAllSteps && allSteps.length > 0 && (
          <div className="max-h-48 overflow-y-auto border-b">
            {allSteps.map((step, idx) => (
              <div 
                key={idx}
                className={`p-3 flex items-center gap-3 border-b last:border-b-0 ${
                  idx === currentStepIndex ? 'bg-blue-50' : ''
                }`}
                onClick={() => {
                  setCurrentStepIndex(idx);
                  if (voiceEnabled && step.instruction) {
                    speak(step.instruction, 'high');
                  }
                }}
              >
                <div className={`p-2 rounded-lg ${idx === currentStepIndex ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>
                  {getManeuverIcon(step.maneuver, 'h-5 w-5')}
                </div>
                <div className="flex-1">
                  <p className={`text-sm ${idx === currentStepIndex ? 'font-semibold' : ''}`}>
                    {step.instruction || 'Continue'}
                  </p>
                </div>
                <span className="text-sm text-gray-500">{step.distanceText || formatDistance(step.distance)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Action Button */}
        <div className="p-4">
          {navigationPhase === 'to_pickup' ? (
            <Button
              onClick={() => onStatusUpdate('start')}
              disabled={isUpdating}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold rounded-xl"
              data-testid="confirm-pickup-btn"
            >
              <CheckCircle className="h-6 w-6 mr-2" />
              {isUpdating ? 'Updating...' : 'Confirm Pickup'}
            </Button>
          ) : (
            <Button
              onClick={() => onStatusUpdate('complete')}
              disabled={isUpdating}
              className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6 text-lg font-semibold rounded-xl"
              data-testid="complete-ride-btn"
            >
              <Flag className="h-6 w-6 mr-2" />
              {isUpdating ? 'Updating...' : 'Complete Ride'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverNavigationView;
